"use client";

import React, { useEffect, useRef, useState } from "react";
import EzoicPlaceholder from "./ezoic/EzoicPlaceholder";

declare global {
  interface Window {
    ezstandalone?: {
      cmd: Array<() => void>;
      showAds?: (placeholderId: number) => void;
    };
  }
}

export type EzoicAdProps = {
  /**
   * Semantic placement id, e.g. "edition_under_intro"
   */
  adId: string;
  className?: string;
};

/**
 * Map logical ad placement ids used in the code (adId)
 * to concrete Ezoic placeholder numeric IDs.
 *
 * IMPORTANT:
 * - Update the numbers on the right to match the IDs
 *   configured in your Ezoic dashboard.
 * - Keys MUST match the adId strings you use in the JSX.
 */
const EZOIC_PLACEHOLDERS: Record<string, number> = {
  // Edition page placements (201-203)
  edition_under_intro: 201,
  edition_mid_content: 202,
  edition_bottom: 203,

  // Event series (master) page placements (211-212)
  series_after_intro: 211,
  series_bottom: 212,

  // Region listing page placements (221-223)
  region_after_intro: 221,
  region_mid_list: 222,
  region_bottom: 223,

  // Province listing page placements (231-232)
  province_after_intro: 231,
  province_mid_list: 232,

  // "Sagre oggi" page placements (241-242)
  oggi_after_intro: 241,
  oggi_mid_list: 242,

  // "Sagre weekend" page placements (251-252)
  weekend_after_intro: 251,
  weekend_mid_list: 252,

  // "Tutte le sagre" / month listings placements (261-262)
  all_sagre_after_intro: 261,
  all_sagre_mid_list: 262,

  // "Vicino a me" page placements (271-272)
  vicino_after_intro: 271,
  vicino_after_map: 272,

  // Home page placements (281-282)
  home_after_hero: 281,
  home_bottom: 282,
};

export function EzoicAd({ adId, className = "" }: EzoicAdProps) {
  const config = EZOIC_PLACEHOLDERS[adId];

  const [isClient, setIsClient] = useState(false);
  const hasRequestedRef = useRef(false);

  // Segnala quando siamo realmente sul client, dopo l'hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Una volta che siamo sul client, chiediamo a Ezoic di riempire lo slot UNA sola volta
  useEffect(() => {
    if (!isClient || !config || hasRequestedRef.current) return;

    hasRequestedRef.current = true;

    try {
      if (typeof window === "undefined") return;

      const anyWindow = window as any;
      anyWindow.ezstandalone = anyWindow.ezstandalone || {};
      anyWindow.ezstandalone.cmd = anyWindow.ezstandalone.cmd || [];

      anyWindow.ezstandalone.cmd.push(() => {
        if (typeof anyWindow.ezstandalone.showAds === "function") {
          anyWindow.ezstandalone.showAds(config);
        } else {
          console.warn("[EZOIC] ezstandalone.showAds non disponibile", {
            adId,
            placeholderId: config,
          });
        }
      });
    } catch (error) {
      console.error("[EZOIC] Errore nel chiamare ezstandalone.showAds", error);
    }
  }, [isClient, config, adId]);

  if (!config) {
    if (typeof window !== "undefined") {
      console.warn("[EZOIC] adId non trovato:", adId);
    }
    return null;
  }

  const placeholderId = config;
  const slotName = adId;
  const combinedClassName = className;

  // Primo render (server + primo pass client): placeholder neutro che non coinvolge ancora Ezoic.
  // In questo stato, React non ha mismatch tra server e client.
  if (!isClient) {
    return <div className={combinedClassName} data-ezoic-placeholder-skeleton={placeholderId} />;
  }

  // Dal secondo render in poi, siamo sicuramente sul client:
  // - Ezoic è stato chiamato via ezstandalone.showAds(config.id)
  // - possiamo rendere il vero placeholder che Ezoic userà
  return <EzoicPlaceholder id={placeholderId} slotName={slotName} className={combinedClassName} />;
}

export default EzoicAd;
